import { useState } from 'react'

import { Box, Button, Skeleton, Text } from '@factorialco/deprecated-design-system'
import { Folder } from '@factorialco/f0-react/icons/app'

import { handleResult } from 'modules/core/lib/handleMutation'
import app from 'modules/core/store/models/app'

import {
  CategoryPolicyRule,
  useDeleteCategoryPolicyRule,
  useReadCategoryPolicyRules,
} from 'modules/expenses/hooks/useCategoryPolicyRules'
import SectionInfo from 'modules/expenses/pages/settings/ExpensePolicy/Components/_SectionInfo'

import DeleteRuleModal from './DeleteRuleModal'
import RuleCard from './RuleCard'
import RuleFormModal from './RuleFormModal'

interface Props {
  categoryId: number
  categoryLabel: string
}

const CategorySettings = ({ categoryId, categoryLabel }: Props) => {
  const { rules, loading } = useReadCategoryPolicyRules(categoryId)
  const { delete: deleteRule } = useDeleteCategoryPolicyRule()

  const [showFormModal, setShowFormModal] = useState(false)
  const [editingRule, setEditingRule] = useState<CategoryPolicyRule | null>(null)
  const [deletingRule, setDeletingRule] = useState<CategoryPolicyRule | null>(null)

  const handleEdit = (rule: CategoryPolicyRule) => {
    setEditingRule(rule)
    setShowFormModal(true)
  }

  const handleCreate = () => {
    setEditingRule(null)
    setShowFormModal(true)
  }

  const handleCloseFormModal = () => {
    setShowFormModal(false)
    setEditingRule(null)
  }

  const handleDelete = async () => {
    if (!deletingRule) return

    const result = await deleteRule(deletingRule.id)
    const handled = handleResult(result, () =>
      app.setFlash({ type: 'wrong', message: 'An error occurred' })
    )

    if (handled.success) {
      app.setFlash({ type: 'correct', message: 'Rule deleted successfully' })
    }
    setDeletingRule(null)
  }

  return (
    <Box flexDirection="column" gap="s24" paddingX="s24" flexGrow>
      <SectionInfo
        icon={Folder}
        title={categoryLabel}
        description="Define spending rules for this category."
      />

      {loading ? (
        <Box flexDirection="column" gap="s12">
          <Skeleton width="full" height="s64" isLoading />
          <Skeleton width="full" height="s64" isLoading />
        </Box>
      ) : (
        <Box flexDirection="column" gap="s16">
          {rules.length === 0 ? (
            <Box
              flexDirection="column"
              alignItems="center"
              padding="s32"
              gap="s8"
              border={{ all: { width: 's1', style: 'solid', color: 'grey400' } }}
              borderRadius={{ all: 'abs008' }}
            >
              <Text.Normal size="300" color="grey600">
                No rules defined yet for this category.
              </Text.Normal>
            </Box>
          ) : (
            <Box flexDirection="column" gap="s12">
              {rules.map((rule) => (
                <RuleCard
                  key={rule.id}
                  rule={rule}
                  onEdit={() => handleEdit(rule)}
                  onDelete={() => setDeletingRule(rule)}
                />
              ))}
            </Box>
          )}

          <Button type="secondary" onClick={handleCreate}>
            Add rule
          </Button>
        </Box>
      )}

      {showFormModal && (
        <RuleFormModal
          categoryId={categoryId}
          rule={editingRule ?? undefined}
          onClose={handleCloseFormModal}
        />
      )}

      {deletingRule && (
        <DeleteRuleModal onSubmit={handleDelete} onClose={() => setDeletingRule(null)} />
      )}
    </Box>
  )
}

export default CategorySettings
